import { useEffect, useState, useMemo } from "react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { useParams, useNavigate } from "react-router-dom";
import { brandService } from "../../services/brand.service";
import { Facebook, Instagram, Youtube, ArrowLeft } from "react-feather";
import TikTokIcon from "../../assets/tiktok";
import { ShowcaseCarousel } from "../../components/ShowcaseCarousel";
import { ProductShowcase } from "../../components/ProductShowcase";
import { HighlightWrapper } from "../../components/HighlightWrapper";
import type {
  BrandDetails as BrandDetailsType,
  AppearanceSettings,
} from "../../types/brand";
import { ICON_SIZES } from "../../types/brand";
import type { ChangedFields } from "../../hooks/useAppearanceHighlight";
import {
  getThemeLayout,
  getThemeStyles,
  processYouTubeContent,
  getAppearanceClasses,
  getColors,
} from "../../utils/themeUtils";
import { DEFAULT_BRAND } from "../../theme/brandTheme";
import { Spinner } from "@/components/ui/spinner";

export function BrandDetail({
  isEmbedded,
  brandId,
  appearanceSettings,
  changedFields,
}: {
  isEmbedded?: boolean;
  brandId?: string | null;
  appearanceSettings?: AppearanceSettings | Partial<AppearanceSettings>;
  changedFields?: ChangedFields;
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brandData, setBrandData] = useState<BrandDetailsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [backendAppearanceSettings, setBackendAppearanceSettings] =
    useState<AppearanceSettings | null>(null);

  // Determine which appearance settings to use
  const effectiveAppearanceSettings = useMemo(() => {
    // If embedded and appearanceSettings prop is provided, use that (for real-time preview)
    if (isEmbedded && appearanceSettings) {
      console.log("appearanceSettings", appearanceSettings);
      return { ...brandData?.brandAppearance, ...appearanceSettings };
    }
    // Otherwise use backend settings
    return backendAppearanceSettings;
  }, [isEmbedded, appearanceSettings, backendAppearanceSettings]);

  // Get CSS classes based on appearance settings
  const appearanceClasses = useMemo(() => {
    return getAppearanceClasses(effectiveAppearanceSettings);
  }, [effectiveAppearanceSettings]);

  // Get color values from appearance settings
  const colors = useMemo(() => {
    return getColors(effectiveAppearanceSettings);
  }, [effectiveAppearanceSettings]);

  // Get theme-specific layout and styles
  const themeLayout = useMemo(() => {
    const theme = effectiveAppearanceSettings?.theme || "TYPE_1";
    return getThemeLayout(theme);
  }, [effectiveAppearanceSettings?.theme]);

  const themeStyles = useMemo(() => {
    const theme = effectiveAppearanceSettings?.theme || "TYPE_1";
    return getThemeStyles(theme, colors);
  }, [effectiveAppearanceSettings?.theme, colors]);

  useEffect(() => {
    const fetchBrandData = async () => {
      if (!id && !brandId) return;
      try {
        const data = await brandService.getAffiliateBrandDetails(
          id || brandId || ""
        );
        // Map the API response to include our required stats
        const mappedData = {
          ...data,
          stats: {
            ...data.stats,
            rank: data.stats?.rank || 9,
            products: data.stats?.products || 126,
            teamSize: data.stats?.team || "-",
            orders: data.stats?.orders || "-",
            joined: data.stats?.joined || "-",
          },
        };
        setBrandData(mappedData);
      } catch (error) {
        console.error("Failed to fetch brand data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAppearanceSettings = async () => {
      try {
        const settings = await brandService.getAppearanceSettings();
        setBackendAppearanceSettings(settings);
      } catch (error) {
        console.error("Failed to fetch appearance settings:", error);
        // Set default appearance settings if fetch fails
        setBackendAppearanceSettings({
          joinText: brandData?.program?.joinText || "",
          joinTitle: brandData?.program?.joinTitle || "",
          ambassadorText: brandData?.program?.ambassadorText || "",
          theme: "TYPE_1",
          socialMediaIconSizes: "medium",
          primayColour: DEFAULT_BRAND.primary,
          secondaryColour: DEFAULT_BRAND.secondary,
          imageIds: [],
          showcaseProductIds: [],
        });
      }
    };

    fetchBrandData();
    fetchAppearanceSettings();
  }, [id, brandId]);

  if (isLoading || !brandData) {
    return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
    );
  }

  // Show a subtle loading indicator if appearance settings are still loading
  const isAppearanceLoading = !effectiveAppearanceSettings && isEmbedded;

  return (
    <div
      className={`min-h-screen ${
        isEmbedded ? "w-full" : ""
      } transition-all duration-500`}
      style={{
        zoom: isEmbedded ? "67%" : "100%",
        backgroundColor: `${colors.secondary}05`,
      }}
    >
      {/* Loading indicator for appearance settings */}
      {isAppearanceLoading && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs animate-pulse">
            Loading styles...
          </div>
        </div>
      )}

      {/* Banner Section */}
      <div className="relative">
        {/* Back Button */}
        {!isEmbedded && (
          <div className="absolute top-8 left-4 z-20">
            <HighlightWrapper
              isHighlighted={changedFields?.primayColour || false}
              color={colors.primary}
            >
              <button
                onClick={() => navigate(-1)}
                className={`inline-flex items-center px-3 py-2 text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105`}
                style={{ backgroundColor: colors.primary }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
            </HighlightWrapper>
          </div>
        )}

        {/* Banner */}
        <HighlightWrapper
          isHighlighted={
            changedFields?.secondaryColour || changedFields?.theme || false
          }
          color={colors.primary}
        >
          <div className={`relative ${themeLayout.bannerHeight}`}>
            {/* Banner Image */}
            {brandData.brandBannerImage && (
              <img
                src={brandData.brandBannerImage}
                alt="Brand Banner"
                className={themeLayout.bannerImage}
              />
            )}
            {/* Banner Overlay */}
            <div className={themeLayout.bannerOverlay} />
            {/* Banner Background Fallback */}
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to bottom, ${colors.secondary}, white)`,
              }}
            />
          </div>
        </HighlightWrapper>
      </div>

      <main className={themeLayout.container}>
        {/* Profile Section */}
        <div
          className={`${themeLayout.profileSection} ${themeLayout.profilePosition}`}
        >
          {/* Profile Image */}
          <HighlightWrapper
            isHighlighted={
              changedFields?.primayColour || changedFields?.theme || false
            }
            color={colors.primary}
          >
            <div
              className={`${themeLayout.profileImage} ${
                effectiveAppearanceSettings?.theme === "TYPE_4"
                  ? "hover:scale-110 hover:-rotate-6 transition-all duration-300 ease-out"
                  : ""
              }`}
            >
              <div className={themeLayout.profileImageSize}>
                <div className="w-full h-full p-2 rounded-full bg-card shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <BrandLogo
                    src={brandData.brandProfileImage}
                    name={
                      brandData.companyName ||
                      `${brandData.firstName ?? ""} ${brandData.lastName ?? ""}`
                    }
                    primaryColor={colors.primary}
                    className="w-full h-full rounded-full text-2xl transition-all duration-300 hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </HighlightWrapper>

          {/* Company Info */}
          <div
            className={`${themeLayout.profileInfo} ${appearanceClasses.spacing}`}
          >
            {/* Company Name and Niches */}
            <div className={`flex flex-col sm:flex-row sm:items-center gap-4`}>
              <h1
                className={`text-[28px] font-semibold text-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
              >
                {brandData.companyName ||
                  brandData.firstName + " " + brandData.lastName}
              </h1>
              <HighlightWrapper
                isHighlighted={changedFields?.primayColour || false}
                color={colors.primary}
              >
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  {brandData.niches?.map((niche) => (
                    <span
                      key={niche.id}
                      className={`${themeStyles.nicheTag} transition-all duration-300 cursor-pointer`}
                      style={{
                        backgroundColor: `${colors.primary}20`,
                        color: colors.primary,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${colors.primary}30`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = `${colors.primary}20`;
                      }}
                    >
                      {niche.name}
                    </span>
                  ))}
                </div>
              </HighlightWrapper>
            </div>

            {/* Website */}
            <div
              className={`mt-2 flex items-center gap-2 justify-center sm:justify-start`}
            >
              <span className="text-muted-foreground">Website:</span>
              {brandData.website && (
                <a
                  href={brandData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-foreground break-all transition-colors duration-300`}
                  style={{ color: "#111827" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#111827";
                  }}
                >
                  {brandData.website.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>

            {/* Socials */}
            <div
              className={`mt-2 flex flex-wrap items-center gap-4 justify-center sm:justify-start`}
            >
              <span className="text-muted-foreground">Socials</span>
              <HighlightWrapper
                isHighlighted={changedFields?.socialMediaIconSizes || false}
                color={colors.primary}
              >
                <div className="flex items-center gap-6">
                  {brandData.social?.facebook && (
                    <a
                      href={brandData.social.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${themeStyles.socialIcon}`}
                    >
                      <Facebook
                        className={
                          ICON_SIZES.find(
                            (size) =>
                              size.label.toUpperCase() ===
                              appearanceSettings?.socialMediaIconSizes
                          )?.size
                        }
                      />
                    </a>
                  )}
                  {brandData.social?.instagram && (
                    <a
                      href={brandData.social.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${themeStyles.socialIcon}`}
                    >
                      <Instagram
                        className={
                          ICON_SIZES.find(
                            (size) =>
                              size.label.toUpperCase() ===
                              appearanceSettings?.socialMediaIconSizes
                          )?.size
                        }
                      />
                    </a>
                  )}
                  {brandData.social?.tiktok && (
                    <a
                      href={brandData.social.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${themeStyles.socialIcon}`}
                    >
                      <TikTokIcon
                        className={
                          ICON_SIZES.find(
                            (size) =>
                              size.label.toUpperCase() ===
                              appearanceSettings?.socialMediaIconSizes
                          )?.size
                        }
                      />
                    </a>
                  )}
                  {brandData.social?.youtube && (
                    <a
                      href={brandData.social.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${themeStyles.socialIcon}`}
                    >
                      <Youtube
                        className={
                          ICON_SIZES.find(
                            (size) =>
                              size.label.toUpperCase() ===
                              appearanceSettings?.socialMediaIconSizes
                          )?.size
                        }
                      />
                    </a>
                  )}
                </div>
              </HighlightWrapper>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={themeLayout.contentGrid}>
          {/* Main Content */}
          <div
            className={`${themeLayout.description} ${themeLayout.statsPosition}`}
          >
            {/* Stats */}
            <HighlightWrapper
              isHighlighted={
                changedFields?.primayColour || changedFields?.theme || false
              }
              color={colors.primary}
            >
              <div className={themeStyles.statsContainer}>
                <div className={themeLayout.statsGrid}>
                  <div
                    className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      style={{ color: colors.primary }}
                    >
                      #{brandData.stats?.rank || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Rank</div>
                  </div>
                  <div
                    className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                      style={{ color: colors.primary }}
                    >
                      {brandData.stats?.products || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Products</div>
                  </div>
                  <div
                    className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      {Number(brandData.stats?.team || 0) >= 1000
                        ? `${Math.floor(
                            Number(brandData.stats?.team || 0) / 1000
                          )}k`
                        : brandData.stats?.team || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Team</div>
                  </div>
                  <div
                    className={`${themeStyles.statsCard} transition-all duration-300 transform hover:scale-105`}
                  >
                    <div
                      className={`text-2xl font-semibold mb-1 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      {Number(brandData.stats?.orders || 0) >= 1000
                        ? `${Math.floor(
                            Number(brandData.stats?.orders || 0) / 1000
                          )}k+`
                        : brandData.stats?.orders || "0"}
                    </div>
                    <div className="text-sm text-muted-foreground">Orders</div>
                  </div>
                </div>
              </div>
            </HighlightWrapper>

            {/* Description */}
            {brandData.description && (
              <div
                className={`${themeStyles.descriptionContainer} ${themeLayout.descriptionPosition}`}
              >
                <div
                  className={`text-muted-foreground prose max-w-none ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  dangerouslySetInnerHTML={{
                    __html: processYouTubeContent(brandData.description),
                  }}
                />
              </div>
            )}

            {/* Showcase Images Carousel */}
            {effectiveAppearanceSettings?.imageIds &&
              effectiveAppearanceSettings.imageIds.length > 0 && (
                <HighlightWrapper
                  isHighlighted={changedFields?.imageIds || false}
                  color={colors.primary}
                >
                  <div
                    className={`${themeStyles.showcaseContainer} ${themeLayout.showcasePosition}`}
                  >
                    <h3
                      className={`text-xl font-semibold mb-4 text-foreground ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                    >
                      Brand Showcase
                    </h3>
                    <ShowcaseCarousel
                      images={effectiveAppearanceSettings.imageIds}
                      autoPlay={true}
                      interval={4000}
                      showControls={true}
                      className={`${appearanceClasses.shadow} ${appearanceClasses.borderRadius}`}
                    />
                  </div>
                </HighlightWrapper>
              )}

            {/* Product Showcase */}
            {effectiveAppearanceSettings?.showcaseProductIds &&
              effectiveAppearanceSettings.showcaseProductIds.length > 0 && (
                <HighlightWrapper
                  isHighlighted={changedFields?.showcaseProductIds || false}
                  color={colors.primary}
                >
                  <div
                    className={`${themeStyles.showcaseContainer} ${themeLayout.showcasePosition}`}
                  >
                    <ProductShowcase
                      selectedProductIds={
                        effectiveAppearanceSettings.showcaseProductIds
                      }
                      appearanceSettings={effectiveAppearanceSettings}
                      className={`${appearanceClasses.shadow} ${appearanceClasses.borderRadius}`}
                    />
                  </div>
                </HighlightWrapper>
              )}
          </div>

          {/* Join Card */}
          <div className={themeLayout.joinCard}>
            <HighlightWrapper
              isHighlighted={
                changedFields?.joinTitle ||
                changedFields?.joinText ||
                changedFields?.primayColour ||
                changedFields?.theme ||
                false
              }
              color={colors.primary}
            >
              <div
                className={`${themeStyles.joinCard} transition-all duration-300 hover:shadow-xl`}
              >
                <HighlightWrapper
                  isHighlighted={changedFields?.joinTitle || false}
                  color={colors.primary}
                >
                  <h2
                    className={`text-xl font-semibold mb-2 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    {effectiveAppearanceSettings?.joinTitle ||
                      brandData.program?.joinTitle ||
                      "Join the team"}
                  </h2>
                </HighlightWrapper>
                <HighlightWrapper
                  isHighlighted={changedFields?.joinText || false}
                  color={colors.primary}
                >
                  <p
                    className={`text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                  >
                    {(() => {
                      const processedContent = processYouTubeContent(
                        effectiveAppearanceSettings?.joinText ||
                          brandData.program?.joinText ||
                          ""
                      );
                      return (
                        <div
                          className="prose max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: processedContent,
                          }}
                        />
                      );
                    })()}
                  </p>
                </HighlightWrapper>
                <div
                  className={`text-sm text-muted-foreground mb-4 ${appearanceClasses.fontFamily} ${appearanceClasses.fontSize} ${appearanceClasses.lineHeight}`}
                >
                  {brandData.program?.commissionValue || "0"}% commission on all
                  code uses
                </div>
                <HighlightWrapper
                  isHighlighted={changedFields?.primayColour || false}
                  color={colors.primary}
                >
                  <button
                    className={`w-full text-white py-2 ${appearanceClasses.borderRadius} ${appearanceClasses.shadow} hover:shadow-md transition-all duration-200 transform hover:scale-105`}
                    style={{
                      backgroundColor: colors.primary,
                      filter: "brightness(1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = "brightness(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = "brightness(1)";
                    }}
                  >
                    Join Now
                  </button>
                </HighlightWrapper>
              </div>
            </HighlightWrapper>
          </div>
        </div>
      </main>

      {/* Enhanced YouTube Embed Styles */}
      <style>{`
        .youtube-embed-wrapper {
          position: relative;
          width: 100%;
          padding-bottom: 56.25%;
          height: 0;
          margin: 12px 0 !important;
          overflow: hidden;
          border-radius: 8px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .youtube-embed-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
          border-radius: 8px;
        }
        
        @media (max-width: 640px) {
          .youtube-embed-wrapper {
            padding-bottom: 65%;
            margin: 8px 0 !important;
          }
        }
        
        /* Rich Text Formatting Styles */
        .prose {
          color: inherit;
        }
        
        .prose p {
          margin: 0.5em 0;
          color: inherit;
        }
        
        .prose a {
          color: rgb(var(--brand-600));
          text-decoration: underline;
        }
        
        .prose a:hover {
          color: rgb(var(--brand-700));
        }
        
        /* Text Alignment - Handle both inline styles and TipTap classes */
        .prose p[style*="text-align: center"],
        .prose p[style*="text-align: center;"],
        .prose .text-center,
        .prose [style*="text-align: center"] {
          text-align: center !important;
        }
        
        .prose p[style*="text-align: right"],
        .prose p[style*="text-align: right;"],
        .prose .text-right,
        .prose [style*="text-align: right"] {
          text-align: right !important;
        }
        
        .prose p[style*="text-align: left"],
        .prose p[style*="text-align: left;"],
        .prose .text-left,
        .prose [style*="text-align: left"] {
          text-align: left !important;
        }
        
        /* Force text alignment with higher specificity */
        .prose div[style*="text-align: center"] { text-align: center !important; }
        .prose div[style*="text-align: right"] { text-align: right !important; }
        .prose div[style*="text-align: left"] { text-align: left !important; }
        
        .prose span[style*="text-align: center"] { text-align: center !important; }
        .prose span[style*="text-align: right"] { text-align: right !important; }
        .prose span[style*="text-align: left"] { text-align: left !important; }
        
        /* Bullet Points and Lists - Handle TipTap's HTML structure */
        .prose ul,
        .prose ul[data-type="bulletList"] {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .prose ol,
        .prose ol[data-type="orderedList"] {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        
        .prose li,
        .prose li[data-type="listItem"] {
          margin: 0.25em 0;
          color: inherit;
        }
        
        /* Bold and Italic */
        .prose strong,
        .prose b,
        .prose [data-type="bold"] {
          font-weight: 600;
          color: inherit;
        }
        
        .prose em,
        .prose i,
        .prose [data-type="italic"] {
          font-style: italic;
          color: inherit;
        }
        
        /* Headings */
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          font-weight: 600;
          margin: 1em 0 0.5em 0;
          color: inherit;
        }
        
        .prose h1 { font-size: 1.5em; }
        .prose h2 { font-size: 1.25em; }
        .prose h3 { font-size: 1.125em; }
        
        /* Links */
        .prose a[href] {
          color: rgb(var(--brand-600));
          text-decoration: underline;
          word-break: break-word;
        }
        
        .prose a[href]:hover {
          color: rgb(var(--brand-700));
        }
        
        /* Blockquotes */
        .prose blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          color: #6b7280;
        }
        
        /* Code */
        .prose code {
          background-color: #f3f4f6;
          padding: 0.125em 0.25em;
          border-radius: 0.25em;
          font-family: monospace;
          font-size: 0.875em;
        }
        
        /* Horizontal Rule */
        .prose hr {
          border: none;
          border-top: 1px solid #e5e7eb;
          margin: 1.5em 0;
        }
        
        /* Additional TipTap-specific styles */
        .prose [data-type="paragraph"] {
          margin: 0.5em 0;
        }
        
        .prose [data-type="heading"] {
          font-weight: 600;
          margin: 1em 0 0.5em 0;
        }
        
        /* Handle TipTap's text alignment attributes */
        .prose [style*="text-align: center"],
        .prose [style*="text-align: center;"] {
          text-align: center !important;
        }
        
        .prose [style*="text-align: right"],
        .prose [style*="text-align: right;"] {
          text-align: right !important;
        }
        
        .prose [style*="text-align: left"],
        .prose [style*="text-align: left;"] {
          text-align: left !important;
        }
        
        /* Handle TipTap's data attributes for text alignment */
        .prose [data-text-align="center"] {
          text-align: center !important;
        }
        
        .prose [data-text-align="right"] {
          text-align: right !important;
        }
        
        .prose [data-text-align="left"] {
          text-align: left !important;
        }
        
        /* Override any conflicting styles */
        .prose * {
          text-align: inherit;
        }
        
        .prose [style*="text-align"] {
          text-align: inherit !important;
        }
      `}</style>
    </div>
  );
}
